import {TitleCasePipe} from "@angular/common";
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {DateRange} from "@angular/material/datepicker";
import {MatDivider} from "@angular/material/divider";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {PanelType} from "../../../../../../../../nter-ui-kit/src/lib/webkit/components/info-panel/info-panel.component";
import {AppCoreModule} from "../../../../../core/modules/app.core.module";
import {DateUtilsService} from "../../../../../shared/services/dateUtils.service";
import {UtilsService} from "../../../../../shared/services/utils.service";
import {LeasePricingPeriod, LeaseSummary} from "../../../leasing.model";
import {
  BookingConfig,
  BookingEstimation,
  GridSpacePricingTimePeriod,
  perPricingPeriod,
  RateType,
  SpaceDefaultRate,
  SpacePricingRateCategory
} from "../../bookings.model";
import {LeaseBookingsService} from "../../lease-bookings.service";

@Component({
  selector: 'app-booking-shared-rates-and-charges',
  standalone: true,
  imports: [
    FlexModule,
    MatDivider,
    MatIcon,
    TitleCasePipe,
    MatSlideToggleModule,
    AppCoreModule,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ],
  templateUrl: './booking-shared-rates-and-charges.component.html',
  styleUrl: './booking-shared-rates-and-charges.component.scss'
})
export class BookingSharedRatesAndChargesComponent implements OnInit {
  @Input() bookingConfig: BookingConfig = new BookingConfig();
  @Input() retailerId?: number;
  @Input() parentPricingPeriods: LeasePricingPeriod[] = [];
  @Input() editType: 'new' | 'add' | 'edit' = 'new';

  @Input() lease: LeaseSummary | null = null;
  @Input() pricingPeriod: LeasePricingPeriod | null = null;

  @Output() previous = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();

  useDefaultPricing: boolean = true;
  periodType: GridSpacePricingTimePeriod | null = null;
  defaultPricingPeriod: LeasePricingPeriod | null = null;

  customRateForm!: FormGroup;
  protected readonly perPricingPeriod = perPricingPeriod;
  protected readonly SpacePricingRateCategory = SpacePricingRateCategory;

  constructor(private bookingsService: LeaseBookingsService,
              private dateUtils: DateUtilsService,
              public utils: UtilsService) {
    this.customRateForm = new FormGroup({
      fixedCategory: new FormControl<SpacePricingRateCategory>(SpacePricingRateCategory.PER_PERIOD, Validators.required),
      variableCategory: new FormControl<SpacePricingRateCategory>(SpacePricingRateCategory.PERCENTAGE, Validators.required),
      fixedRate: new FormControl<number>(0, Validators.required),
      variableRate: new FormControl<number>(0, Validators.required)
    })
  }

  ngOnInit() {
    this.defaultPricingPeriod = this.setDefaultPricingPeriod();

    if (!this.hasDefaultPricing) this.useDefaultPricing = false;

    if (this.editType == 'edit' && this.lease && this.pricingPeriod) {
      this.generateDefaultPricing();
    }

    if (this.bookingConfig.bookingRates.length > 0) {
      this.setExistingPricing();
    } else {
      this.bookingConfig.spaceIds.forEach(ea => this.toggleDefaultPricing());
    }

    this.setTimePeriod();
    this.setBookingEstimation();

    this.customRateForm.valueChanges.subscribe(change => {
      this.setBookingEstimation();
    })
  }

  get bookingEstimation() {
    return this.bookingConfig.bookingEstimation;
  }

  get leasePeriodDays(): number {
    const start = new Date(this.bookingConfig.selectedDates[0]);
    const end = new Date(this.bookingConfig.selectedDates[this.bookingConfig.selectedDates.length - 1]);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }

  get leasePeriods(): number {
    switch (this.periodType) {
      case GridSpacePricingTimePeriod.DAILY:
        return this.bookingConfig.selectedDates.length;
      case GridSpacePricingTimePeriod.WEEKLY:
        return this.dateUtils.getNumberOfPeriods(this.bookingConfig.startDate, this.bookingConfig.endDate, 'week');
      case GridSpacePricingTimePeriod.WEEKEND:
        return this.dateUtils.getNumberOfWeekends(this.bookingConfig.startDate, this.bookingConfig.endDate, this.bookingConfig.includeDays.includes('Friday'));
      case GridSpacePricingTimePeriod.MONTHLY:
        return this.dateUtils.getNumberOfPeriods(this.bookingConfig.startDate, this.bookingConfig.endDate, 'month');
    }

    return 0;
  }

  get customRentPerPeriod(): number {
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_SQM) {
      return this.effectiveTotalSpace * this.customRateForm.get('fixedRate')?.value;
    }
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_PERIOD) {
      return this.customRateForm.get('fixedRate')?.value;
    }
    return 0;
  }

  get customEstimatedCost(): number {
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_SQM) {
      return this.leasePeriods * this.effectiveTotalSpace * this.customRateForm.get('fixedRate')?.value;
    }
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_PERIOD) {
      return this.leasePeriods * this.customRateForm.get('fixedRate')?.value;
    }
    return 0;
  }

  get customPercentageCost(): string {
    const variableRate = this.customRateForm.get('variableRate')?.value;
    if (variableRate > 0) return '+ ' + variableRate + '% turnover';
    return '';
  }

  get effectiveTotalSpace(): number {
    return this.bookingConfig.subLeaseSize;
  }

  get canSubmit(): boolean {
    return this.bookingEstimation != null
      && (this.useDefaultPricing || (!this.useDefaultPricing && this.customRateForm.valid));
  }

  setExistingPricing() {
    this.useDefaultPricing = !this.bookingConfig.customRates;
    if (!this.useDefaultPricing) {
      if (this.bookingConfig.bookingEstimation) {
        this.customRateForm.patchValue({
          fixedCategory: this.bookingConfig.bookingEstimation?.fixedCategoryType,
          variableCategory: this.bookingConfig.bookingEstimation?.variableCategoryType,
          fixedRate: this.bookingConfig.bookingEstimation?.fixedRate,
          variableRate: this.bookingConfig.bookingEstimation?.variableRate,
        })
      }
    }
  }

  generateDefaultPricing() {
    if (!this.pricingPeriod?.bookingPricing) {
      this.useDefaultPricing = false;
    }
  }

  setBookingEstimation() {
    if (!this.useDefaultPricing) {
      const fixedCat: SpacePricingRateCategory = this.customRateForm.get('fixedCategory')!.value;
      const fixedRate: number = this.customRateForm.get('fixedRate')!.value;
      const bookingEstimation: BookingEstimation = {
        effectiveSpaces: 'Custom',
        totalSpace: this.effectiveTotalSpace,
        periodType: this.periodType!,
        periodTypeUnit: this.periodTypeUnit!,
        leasePeriodDays: this.leasePeriodDays,
        leasePeriods: this.leasePeriods,
        fixedRate: this.customRateForm.get('fixedRate')!.value,
        variableRate: this.customRateForm.get('variableRate')!.value,
        fixedCategoryType: this.customRateForm.get('fixedCategory')!.value,
        variableCategoryType: this.customRateForm.get('variableCategory')!.value,
        fixedCost: this.customEstimatedCost,
        variableCost: this.customPercentageCost,
        rentPerPeriod: fixedCat == SpacePricingRateCategory.PER_SQM ? fixedRate * this.effectiveTotalSpace : fixedRate
      }

      this.bookingConfig.bookingEstimation = {...bookingEstimation};
    } else {
      const bookingEstimation: BookingEstimation = {
        effectiveSpaces: '',
        totalSpace: this.effectiveTotalSpace,
        periodType: this.periodType!,
        periodTypeUnit: this.periodTypeUnit,
        leasePeriodDays: this.leasePeriodDays,
        leasePeriods: this.leasePeriods,
        fixedRate: this.periodRate,
        variableRate: this.hasDefaultPricing ? this.defaultPricingPeriod!.rentalPricingTurnoverPercentageValue! : 0,
        fixedCategoryType: this.fixedRateType!,
        variableCategoryType: SpacePricingRateCategory.PERCENTAGE,
        fixedCost: this.estimatedCost,
        variableCost: this.percentageCost,
        rentPerPeriod: this.fixedRateType == SpacePricingRateCategory.PER_SQM ? this.periodRate * this.effectiveTotalSpace : this.periodRate
      }

      this.bookingConfig.bookingEstimation = {...bookingEstimation};
    }
  }

  setTimePeriod() {
    const period = Object.values(GridSpacePricingTimePeriod)
      .find(key => perPricingPeriod[key].patternType.includes(this.bookingConfig.patternType));
    if (period) this.periodType = period;
  }

  toggleDefaultPricing() {
    this.useDefaultPricing = !this.useDefaultPricing;
  }

  get hasDefaultPricing(): boolean {
    return !!this.defaultPricingPeriod && !!this.defaultPricingPeriod.bookingPricing;
  }

  get periodTypeRate(): GridSpacePricingTimePeriod {
    const patternType = this.bookingConfig!.patternType;
    return this.bookingsService.getPatternTimePeriod(patternType)!;
  }

  get periodTypeUnit(): string {
    return perPricingPeriod[this.periodTypeRate].period;
  }

  get fixedRateType(): SpacePricingRateCategory | null {
    return this.defaultPricingPeriod!.bookingPricing!.spacePricingRateCategory;
  }

  get rentPerPeriod(): number {
    const cat = this.defaultPricingPeriod!.bookingPricing!.spacePricingRateCategory;

    if (cat == SpacePricingRateCategory.PER_SQM) {
      return this.defaultPricingPeriod!.fixedRentalPricingValue! * this.bookingConfig.subLeaseSize;
    }

    if (cat == SpacePricingRateCategory.PER_PERIOD) {
      return this.defaultPricingPeriod!.fixedRentalPricingValue!;
    }

    return 0;
  }

  get estimatedCost(): number {
    const cat = this.defaultPricingPeriod!.bookingPricing!.spacePricingRateCategory;

    if (cat == SpacePricingRateCategory.PER_SQM) {
      return this.leasePeriods * this.defaultPricingPeriod!.fixedRentalPricingValue! * this.bookingConfig.subLeaseSize;
    }

    if (cat == SpacePricingRateCategory.PER_PERIOD) {
      return this.leasePeriods * this.defaultPricingPeriod!.fixedRentalPricingValue!;
    }

    return 0;
  }

  get percentageCost(): string {
    if (!this.defaultPricingPeriod!.rentalPricingTurnoverPercentageValue!) return '';
    return '+ ' + this.defaultPricingPeriod!.rentalPricingTurnoverPercentageValue! + '% turnover';
  }

  get periodRate(): number {
    return this.defaultPricingPeriod!.fixedRentalPricingValue!;
  }

  prevStep() {
    this.previous.emit();
  }

  nextStep() {
    if (this.bookingEstimation) {
      if (!this.useDefaultPricing) {
        const fixedRate: SpaceDefaultRate = {
          id: 0,
          spaceId: 0,
          rateType: RateType.FIXED,
          category: this.bookingEstimation.fixedCategoryType,
          timePeriod: this.bookingEstimation.periodType,
          value: this.bookingEstimation.fixedRate,
          isDefault: true,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
        }
        const variableRate: SpaceDefaultRate = {
          id: 0,
          spaceId: 0,
          rateType: RateType.VARIABLE,
          category: this.bookingEstimation.variableCategoryType,
          timePeriod: this.bookingEstimation.periodType,
          value: this.bookingEstimation.variableRate,
          isDefault: true,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
        }
        this.bookingConfig.bookingRates = [fixedRate, variableRate];
        this.bookingConfig.spacePricingRateCategory = fixedRate.category;
        this.bookingConfig.customRates = true;
      }

      if (this.useDefaultPricing && this.defaultPricingPeriod && this.defaultPricingPeriod.bookingPricing) {
        this.bookingConfig.bookingRates = [...this.defaultPricingPeriod.bookingPricing.bookingRates];
        this.bookingConfig.spacePricingRateCategory = this.defaultPricingPeriod.bookingPricing.spacePricingRateCategory;
        this.bookingConfig.customRates = false;
      }
    }
    this.next.emit();
  }

  setDefaultPricingPeriod() {
    let defaultPeriod;
    // no default if parent does not have any rates & charges
    if (this.parentPricingPeriods.length == 0) {
      return null;
    }
    const periodsForPatternType = this.parentPricingPeriods
      .filter(f => f.bookingPricing!.patternType == this.bookingConfig.patternType);
    // no default of parent does not have any rates & charges with matching pattern type
    if (periodsForPatternType.length == 0) {
      return null;
    }

    periodsForPatternType.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

    const subLeaseRange = new DateRange(this.bookingConfig.startDate, this.bookingConfig.endDate);
    const overlappingPeriods = periodsForPatternType.filter(f => {
      const pricingPeriodRange = new DateRange(f.startDate, f.endDate);
      return this.dateUtils.doesOverlap(subLeaseRange, pricingPeriodRange);
    })

    // if none overlap by date - use latest pricing period
    if (overlappingPeriods.length == 0) {
      return periodsForPatternType[0];
    } else {
      // if 1 or more, use latest pricing period
      return overlappingPeriods[0];
    }
  }

  addCustomRate() {
    this.useDefaultPricing = false;
  }

  protected readonly PanelType = PanelType;
}
